import axios from 'axios';
import * as Sentry from '@sentry/react';

/**
 * Traducción única de cualquier error de la API a (a) un mensaje que el usuario
 * pueda accionar y (b) contexto estructurado para el log.
 *
 * Sin esto cada pantalla inventa su propio cartel genérico ("No se pudieron
 * cargar las escrituras") y se pierde la única información que sirve para
 * diagnosticar: si fue 429, 500, timeout o falta de conexión.
 */
export interface ApiErrorInfo {
  /** HTTP status, o null si la request nunca llegó a destino */
  status: number | null;
  /** `error_code` del backend, cuando lo manda */
  errorCode: string | null;
  /** Mensaje para mostrarle al usuario, en español */
  message: string;
  /** Detalle adicional (errores de validación), si hay */
  detail: string | null;
  /** Segundos a esperar antes de reintentar (429) */
  retryAfterSeconds: number | null;
  /** La request fue abortada a propósito: no es una falla */
  isCanceled: boolean;
  isNetwork: boolean;
  isTimeout: boolean;
  isRateLimited: boolean;
  /** 401 o 403: no tiene sentido reintentar solo */
  isAuth: boolean;
  /** Reintentar tiene chances razonables de funcionar */
  isRetryable: boolean;
  /** Texto técnico para Sentry y consola, nunca para el usuario */
  technicalMessage: string;
}

const GENERIC_MESSAGE = 'Ocurrió un error inesperado. Volvé a intentar en unos segundos.';

const STATUS_MESSAGES: Record<number, string> = {
  400: 'La consulta tiene datos inválidos.',
  401: 'Tu sesión expiró. Volvé a iniciar sesión.',
  403: 'No tenés permisos para ver esta información.',
  404: 'No encontramos lo que estabas buscando.',
  409: 'La información cambió mientras trabajabas. Actualizá y volvé a intentar.',
  413: 'El archivo supera el tamaño máximo permitido.',
  422: 'Hay datos inválidos en el formulario.',
  429: 'Demasiadas consultas seguidas. Esperá unos segundos y volvé a intentar.',
  500: 'El servidor tuvo un problema procesando la consulta.',
  502: 'El servidor no está respondiendo. Reintentá en un momento.',
  503: 'El servicio no está disponible en este momento.',
  504: 'El servidor tardó demasiado en responder.',
};

/** Una request cancelada (cambio de filtro, desmontaje) no es una falla. */
export const isCanceledError = (error: unknown): boolean => {
  if (!error) return false;
  if (axios.isCancel?.(error)) return true;

  const candidate = error as { name?: string; code?: string };
  return candidate?.name === 'CanceledError'
    || candidate?.name === 'AbortError'
    || candidate?.code === 'ERR_CANCELED';
};

const parseRetryAfter = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.ceil(parsed) : null;
};

/** Aplana los errores de validación de Laravel a una sola línea legible. */
const flattenValidationErrors = (errors: unknown): string | null => {
  if (!errors || typeof errors !== 'object') return null;

  const messages = Object.values(errors as Record<string, unknown>)
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((value): value is string => typeof value === 'string' && value.trim() !== '');

  return messages.length > 0 ? messages.join(' ') : null;
};

export const describeApiError = (error: unknown): ApiErrorInfo => {
  const base: ApiErrorInfo = {
    status: null,
    errorCode: null,
    message: GENERIC_MESSAGE,
    detail: null,
    retryAfterSeconds: null,
    isCanceled: false,
    isNetwork: false,
    isTimeout: false,
    isRateLimited: false,
    isAuth: false,
    isRetryable: false,
    technicalMessage: '',
  };

  if (isCanceledError(error)) {
    return { ...base, isCanceled: true, message: '', technicalMessage: 'Request cancelada' };
  }

  if (!axios.isAxiosError(error)) {
    const message = error instanceof Error ? error.message : String(error ?? '');
    return { ...base, technicalMessage: message || 'Error desconocido' };
  }

  const response = error.response;
  const technicalMessage = error.message || 'Error de red';

  // Sin respuesta: la request nunca llegó o el servidor cortó la conexión.
  if (!response) {
    const isTimeout = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';

    return {
      ...base,
      isNetwork: !isTimeout,
      isTimeout,
      isRetryable: true,
      message: isTimeout
        ? 'La consulta tardó demasiado y se canceló. Reintentá en un momento.'
        : 'No pudimos conectarnos con el servidor. Revisá tu conexión y reintentá.',
      technicalMessage,
    };
  }

  const status = response.status;
  const data = (response.data ?? {}) as Record<string, unknown>;
  const errorCode = typeof data.error_code === 'string' ? data.error_code : null;
  const detail = flattenValidationErrors(data.errors);

  const retryAfterSeconds = parseRetryAfter(
    data.retry_after ?? response.headers?.['retry-after'],
  );

  // El backend manda mensajes pensados para el usuario junto con `error_code`.
  // Si no viene uno propio, se usa el mapa por status.
  const serverMessage = typeof data.message === 'string' && data.message.trim() !== ''
    ? data.message.trim()
    : null;

  let message = serverMessage ?? STATUS_MESSAGES[status] ?? GENERIC_MESSAGE;

  if (status === 429) {
    const wait = retryAfterSeconds;
    message = wait && wait > 1
      ? `Demasiadas consultas seguidas. Reintentá en ${wait} segundos.`
      : STATUS_MESSAGES[429];
  }

  return {
    status,
    errorCode,
    message,
    detail,
    retryAfterSeconds,
    isCanceled: false,
    isNetwork: false,
    isTimeout: false,
    isRateLimited: status === 429,
    isAuth: status === 401 || status === 403,
    isRetryable: status === 429 || status === 408 || status >= 500,
    technicalMessage,
  };
};

export const getApiErrorMessage = (error: unknown, fallback = GENERIC_MESSAGE): string => {
  const info = describeApiError(error);
  if (info.isCanceled) return '';

  return info.detail ? `${info.message} ${info.detail}` : (info.message || fallback);
};

/**
 * Status que no aportan nada en Sentry: son parte del funcionamiento normal
 * (sesión vencida, permiso faltante, validación de formulario).
 */
const NOT_WORTH_REPORTING = [401, 403, 404, 422];

export interface ReportApiErrorContext {
  /** Qué se estaba haciendo, en términos del dominio: "cargar columna kanban" */
  operation: string;
  extra?: Record<string, unknown>;
}

/**
 * Un mismo error puede pasar por dos manos (el `onError` de la mutation y el
 * handler global del QueryClient). Sin esto, Sentry recibe el evento duplicado
 * y las métricas de incidencia quedan al doble.
 */
const alreadyReported = new WeakSet<object>();

const markReported = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  if (alreadyReported.has(error)) return true;

  alreadyReported.add(error);
  return false;
};

/**
 * Manda el error a Sentry con contexto útil. Devuelve la descripción para que
 * quien llama pueda usar el mismo mensaje que se reportó.
 */
export const reportApiError = (
  error: unknown,
  { operation, extra }: ReportApiErrorContext,
): ApiErrorInfo => {
  const info = describeApiError(error);

  if (info.isCanceled) return info;

  const isDuplicate = markReported(error);
  const shouldReport =
    !isDuplicate && (info.status === null || !NOT_WORTH_REPORTING.includes(info.status));

  if (shouldReport) {
    Sentry.captureException(error, {
      level: info.isRateLimited ? 'warning' : 'error',
      tags: {
        operation,
        api_status: info.status === null ? 'sin_respuesta' : String(info.status),
        api_error_code: info.errorCode ?? 'none',
      },
      extra: {
        operation,
        status: info.status,
        errorCode: info.errorCode,
        retryAfterSeconds: info.retryAfterSeconds,
        isTimeout: info.isTimeout,
        isNetwork: info.isNetwork,
        technicalMessage: info.technicalMessage,
        ...extra,
      },
    });
  }

  // Deja rastro también en la consola del navegador: es lo primero que mira
  // quien está sentado adelante cuando le reportan el problema.
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.error(`[api] ${operation} falló`, { info, error });
  }

  return info;
};
